import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImageUploadField - A reusable component for image upload with URL input fallback
 * 
 * Props:
 * - value: Current image URL
 * - onChange: Callback when URL changes (receives the new URL)
 * - label: Label text (default: "Image")
 * - placeholder: Placeholder text for URL input
 * - showPreview: Whether to show image preview (default: true)
 * - previewAspect: Aspect ratio for preview - "video" | "square" | "auto" (default: "video")
 * - requireAuth: Whether upload requires authentication (default: true for admin uploads)
 * - disabled: Disable the field
 * - className: Additional CSS classes
 */
const ImageUploadField = ({
  value = '',
  onChange,
  label = 'Image',
  placeholder = 'https://... or upload',
  showPreview = true,
  previewAspect = 'video',
  requireAuth = true,
  disabled = false,
  className = ''
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = {
        'Content-Type': 'multipart/form-data'
      };

      // Use public endpoint if auth not required, otherwise use admin endpoint
      let uploadUrl = `${API_URL}/upload/public-image`;
      
      if (requireAuth) {
        uploadUrl = `${API_URL}/upload/image`;
      }

      const response = await axios.post(uploadUrl, formData, { headers });

      // Handle both S3 URLs (complete) and local URLs (relative)
      let imageUrl = response.data.url;
      if (response.data.storage === 'local' && !imageUrl.startsWith('http')) {
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      }

      onChange(imageUrl);
      toast.success(`Image uploaded to ${response.data.storage === 's3' ? 'S3' : 'server'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange('');
  };

  const aspectClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    auto: 'min-h-[120px]'
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="text-sm font-medium">{label}</label>}
      
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled || uploading}
          className="flex-1"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          title="Upload image"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
        </Button>
      </div>

      {showPreview && value && (
        <div className={`relative group rounded-lg overflow-hidden bg-muted ${aspectClasses[previewAspect]}`}>
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Change
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </div>
        </div>
      )}

      {showPreview && !value && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer ${aspectClasses[previewAspect]} flex items-center justify-center`}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <div className="flex flex-col items-center gap-2">
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                <span className="text-sm text-muted-foreground">Uploading...</span>
              </>
            ) : (
              <>
                <ImageIcon className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Click to upload or paste URL</span>
                <span className="text-xs text-muted-foreground">JPEG, PNG, WebP, GIF (max 5MB)</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
